import React, { useState, useEffect } from 'react';
import {
    Layout,
    Card,
    Row,
    Col,
    Typography,
    Button,
    DatePicker,
    Select,
    Statistic,
    List,
    Progress,
    Tabs,
    Tag
} from 'antd';
import {
    ArrowUpOutlined,
    ArrowDownOutlined,
    EllipsisOutlined,
    InfoCircleOutlined,
    SearchOutlined,
    ReloadOutlined
} from '@ant-design/icons';
import { Area, Column, Pie, Bar, Line } from '@ant-design/charts';
import moment from 'moment';
import { getTypesenseClient } from "../../utils/typesenseClient";

const { Header, Content } = Layout;
const { Title, Text } = Typography;
const { RangePicker } = DatePicker;
const { Option } = Select;
const { TabPane } = Tabs;



const Dashboard = (props) => {
    const [loading, setLoading] = useState(false);
    const [stats, setStats] = useState({
        today: { value: 0, change: 0 },
        yesterday: { value: 0, change: 0 },
        month: { value: 0, change: 0 },
        last7Days: { value: 0, change: 0 },
        last30Days: { value: 0, change: 0 }
    });
    const [trendData, setTrendData] = useState([]);
    const [overallTrendData, setOverallTrendData] = useState([]);
    const [rankingData, setRankingData] = useState([]);
    const [occupationData, setOccupationData] = useState([]);
    const [shippingData, setShippingData] = useState([]);
    const [cancelReturnData, setCancelReturnData] = useState([]);
    const [userTabsData, setUserTabsData] = useState([]);
    const [selectedDate, setSelectedDate] = useState(moment());

    useEffect(() => {
        const fetchOrderCount = async () => {
            setLoading(true);
            try {
                let toOrg = "";
                let fromOrg = "";

                if (props.userOrg && props.userOrg.reseller && props.org && props.org.buyer) {
                    toOrg = props.userOrg?.id;
                    fromOrg = props.org.id;
                } else if (props.userOrg && props.userOrg.maker && props.org && props.org.reseller) {
                    toOrg = props.userOrg?.id;
                    fromOrg = props.org.id;
                } else if (props.userOrg && props.userOrg.reseller && props.org && props.org.maker) {
                    toOrg = props.org.id;
                    fromOrg = props.userOrg?.id;
                } else if (props.userOrg && props.userOrg.buyer && props.org && props.org.reseller) {
                    toOrg = props.org.id;
                    fromOrg = props.userOrg?.id;
                }

                if (!fromOrg || !toOrg) {
                    console.warn("Organization filters could not be determined.");
                    return;
                }

                const orgFilter = `fromOrganization:${fromOrg} && toOrganization:${toOrg} && applied:true`;


                // --- Date Intervals ---
                const todayStart = moment().startOf('day').unix();
                const yesterdayStart = moment().subtract(1, 'days').startOf('day').unix();
                const dayBeforeYesterdayStart = moment().subtract(2, 'days').startOf('day').unix();

                // Month (Selected)
                const monthStart = selectedDate.clone().startOf('month').unix();
                const prevMonthStart = selectedDate.clone().subtract(1, 'months').startOf('month').unix();

                // Last 7 Days (Including Today)
                const last7DaysStart = moment().subtract(6, 'days').startOf('day').unix();
                // Prev 7 Days (The 7 days before the last 7 days)
                const prev7DaysStart = moment().subtract(13, 'days').startOf('day').unix();

                // --- Queries ---
                const createQuery = (start, end) => ({
                    'q': '*',
                    'query_by': 'cartItemNames',
                    'filter_by': `${orgFilter} && sortDate._seconds:>=${start} && sortDate._seconds:<${end}`,
                    'per_page': 0
                });

                // 1. Today (Today Start -> Now/End of Day)
                const todayQuery = createQuery(todayStart, moment().endOf('day').unix() + 1);
                // 2. Yesterday (Yesterday Start -> Today Start)
                const yesterdayQuery = createQuery(yesterdayStart, todayStart);
                // 3. Day Before Yesterday
                const dayBeforeYesterdayQuery = createQuery(dayBeforeYesterdayStart, yesterdayStart);

                // 4. This Month (Selected)
                // Note: The original code used just `>=` for the current month. If we want strict month boundaries:
                const monthEnd = selectedDate.clone().endOf('month').unix() + 1;
                const monthQuery = createQuery(monthStart, monthEnd);

                // 5. Prev Month
                const prevMonthQuery = createQuery(prevMonthStart, monthStart);

                // 6. Last 7 Days
                const last7DaysQuery = createQuery(last7DaysStart, moment().endOf('day').unix() + 1);
                // 7. Prev 7 Days Window
                const prev7DaysQuery = createQuery(prev7DaysStart, last7DaysStart);

                // 8. Last 30 Days
                const last30DaysStart = moment().subtract(29, 'days').startOf('day').unix();
                const last30DaysQuery = createQuery(last30DaysStart, moment().endOf('day').unix() + 1);
                // 9. Prev 30 Days
                const prev30DaysStart = moment().subtract(59, 'days').startOf('day').unix();
                const prev30DaysQuery = createQuery(prev30DaysStart, last30DaysStart);

                // 10. Trend (Last 12 Months)
                // We want data month by month for the last 12 months.
                // Start from start of 11 months ago (so 12 months total including current)
                const trendStart = moment().subtract(11, 'months').startOf('month').unix();
                const trendQuery = {
                    'q': '*',
                    'query_by': 'cartItemNames',
                    'filter_by': `${orgFilter} && sortDate._seconds:>=${trendStart}`,
                    'per_page': 250 // Fetch actual hits to calculate totals. Warning: Scaling issue if > 250 orders.
                };

                // 11. User Stats (Last 6 Months)
                // Replaces Ranking Query
                const userStatsStart = moment().subtract(6, 'months').startOf('month').unix();

                // 11. Ranking (Last 6 Months Items)
                // Restored for "Top Items" card
                const rankingQuery = {
                    'q': '*',
                    'query_by': 'cartItemNames',
                    'filter_by': `${orgFilter} && sortDate._seconds:>=${userStatsStart}`,
                    'per_page': 250
                };

                // 11. User Stats (Last 6 Months)
                // Replaces Ranking Query
                const userStatsQuery = {
                    'q': '*',
                    'query_by': 'fromUserName',
                    'filter_by': `${orgFilter} && sortDate._seconds:>=${userStatsStart}`,
                    'per_page': 250 // Warning: Scalability limit
                };

                // 12. Cancel / Return (Last 3 Months)
                const cancelReturnStart = moment().subtract(3, 'months').startOf('month').unix();
                const cancelReturnQuery = {
                    'q': '*',
                    'query_by': 'cartItemNames',
                    'filter_by': `${orgFilter} && sortDate._seconds:>=${cancelReturnStart}`,
                    'per_page': 250
                };

                const typesense = await getTypesenseClient();
                const [
                    todayRes,
                    yesterdayRes,
                    dayBeforeYesRes,
                    monthRes,
                    prevMonthRes,
                    last7Res,
                    prev7Res,
                    last30Res,
                    prev30Res,
                    trendRes,
                    userStatsRes,
                    rankingRes,
                    cancelReturnRes
                ] = await Promise.all([
                    typesense.collections('orders').documents().search(todayQuery),
                    typesense.collections('orders').documents().search(yesterdayQuery),
                    typesense.collections('orders').documents().search(dayBeforeYesterdayQuery),
                    typesense.collections('orders').documents().search(monthQuery),
                    typesense.collections('orders').documents().search(prevMonthQuery),
                    typesense.collections('orders').documents().search(last7DaysQuery),
                    typesense.collections('orders').documents().search(prev7DaysQuery),
                    typesense.collections('orders').documents().search(last30DaysQuery),
                    typesense.collections('orders').documents().search(prev30DaysQuery),
                    typesense.collections('orders').documents().search(trendQuery),
                    typesense.collections('orders').documents().search(userStatsQuery),
                    typesense.collections('orders').documents().search(rankingQuery),
                    typesense.collections('orders').documents().search(cancelReturnQuery)
                ]);

                // Helper to calculate % change
                const calcChange = (current, prev) => {
                    if (prev > 0) return ((current - prev) / prev) * 100;
                    if (current > 0) return 100;
                    return 0;
                };

                const todayVal = todayRes.found || 0;
                const yesterdayVal = yesterdayRes.found || 0;
                const dayBeforeVal = dayBeforeYesRes.found || 0;

                const monthVal = monthRes.found || 0;
                const prevMonthVal = prevMonthRes.found || 0;

                const last7Val = last7Res.found || 0;
                const prev7Val = prev7Res.found || 0;

                const last30Val = last30Res.found || 0;
                const prev30Val = prev30Res.found || 0;

                setStats({
                    today: {
                        value: todayVal, change: calcChange(todayVal, yesterdayVal)
                    },
                    yesterday: { value: yesterdayVal, change: calcChange(yesterdayVal, dayBeforeVal) },
                    month: { value: monthVal, change: calcChange(monthVal, prevMonthVal) },
                    last7Days: { value: last7Val, change: calcChange(last7Val, prev7Val) },
                    last30Days: { value: last30Val, change: calcChange(last30Val, prev30Val) }
                });

                // --- Process Trend Data (Multi-line & Overall) ---
                // Map: "YYYY-MM" -> { shipped: 0, unshipped: 0, cancel: 0, return: 0, totalSales: 0 }
                const trendMap = {};
                // Initialize last 12 months
                for (let i = 11; i >= 0; i--) {
                    const mKey = moment().subtract(i, 'months').format('MMM YYYY');
                    trendMap[mKey] = { shipped: 0, unshipped: 0, cancel: 0, return: 0, totalSales: 0 };
                }

                if (trendRes && trendRes.hits) {
                    trendRes.hits.forEach(hit => {
                        const doc = hit.document;
                        const dateUnix = doc.sortDate && doc.sortDate._seconds ? doc.sortDate._seconds : 0;
                        const mKey = moment.unix(dateUnix).format('MMM YYYY');

                        let isCancel = false;
                        let isReturn = false;
                        let orderSales = 0;
                        let items = [];

                        // Robust cart parsing
                        if (doc.cart && doc.cart.items && Array.isArray(doc.cart.items)) {
                            items = doc.cart.items;
                        } else if (doc.items && Array.isArray(doc.items)) {
                            items = doc.items;
                        } else if (doc.cart && typeof doc.cart === 'string') {
                            try {
                                const parsedCart = JSON.parse(doc.cart);
                                if (parsedCart && Array.isArray(parsedCart)) {
                                    items = parsedCart;
                                } else if (parsedCart && parsedCart.items && Array.isArray(parsedCart.items)) {
                                    items = parsedCart.items;
                                }
                            } catch (e) {
                                console.warn("Failed to parse cart JSON for trend:", e);
                            }
                        }

                        if (items && items.length > 0) {
                            items.forEach(item => {
                                const itemTotal = (item.count || 0) * (item.price || 0);
                                if (item.cancelConfirm === true) {
                                    isCancel = true;
                                } else if (item.returnConfirm === true) {
                                    isReturn = true;
                                } else {
                                    orderSales += itemTotal;
                                }
                            });
                        }

                        if (trendMap.hasOwnProperty(mKey)) {
                            // Accumulate Sales Value (Overall Trend)
                            trendMap[mKey].totalSales += orderSales;

                            // Accumulate Cancel/Return (Count of Orders)
                            if (isCancel) trendMap[mKey].cancel += 1;
                            if (isReturn) trendMap[mKey].return += 1;

                            // Accumulate Shipped/Unshipped (Count of Orders)
                            if (doc.isShipped === true) {
                                trendMap[mKey].shipped += 1;
                            } else {
                                trendMap[mKey].unshipped += 1;
                            }
                        }
                    });
                }

                // 1. Format for Order Trend (Line Chart - 12 Months, Count)
                const finalTrendData = [];
                for (let i = 11; i >= 0; i--) {
                    const mKey = moment().subtract(i, 'months').format('MMM YYYY');
                    const data = trendMap[mKey] || { shipped: 0, unshipped: 0, cancel: 0, return: 0 };

                    finalTrendData.push({ month: mKey, type: 'Shipped', value: data.shipped });
                    finalTrendData.push({ month: mKey, type: 'Unshipped', value: data.unshipped });
                    finalTrendData.push({ month: mKey, type: 'Cancel', value: data.cancel });
                    finalTrendData.push({ month: mKey, type: 'Return', value: data.return });
                }
                setTrendData(finalTrendData);

                // 2. Format for Overall Trend (Area Chart - Last 6 Months, Sales Value)
                const finalOverallData = [];
                for (let i = 5; i >= 0; i--) {
                    const mKey = moment().subtract(i, 'months').format('MMM YYYY');
                    const data = trendMap[mKey] || { totalSales: 0 };
                    finalOverallData.push({
                        month: mKey,
                        sales: data.totalSales
                    });
                }
                setOverallTrendData(finalOverallData);


                // --- Process User Stats (Last 6 Months) ---
                // Replaces Ranking Data (Project Ranking) & Occupation Data (User Order)
                // Also prepares data for User Detailed Analysis Tabs
                const userMap = {}; // UserName -> count
                const userTrendMap = {}; // UserName -> { 'MMM YYYY': count }

                // Initialize months for trends (ensure existing keys)
                const last6MonthsKeys = [];
                for (let i = 5; i >= 0; i--) {
                    last6MonthsKeys.push(moment().subtract(i, 'months').format('MMM YYYY'));
                }

                if (userStatsRes && userStatsRes.hits) {
                    userStatsRes.hits.forEach(hit => {
                        const doc = hit.document;
                        const userName = doc.fromUserName || "Unknown User";

                        // Total Count
                        userMap[userName] = (userMap[userName] || 0) + 1;

                        // Trend Count
                        const dateUnix = doc.sortDate && doc.sortDate._seconds ? doc.sortDate._seconds : 0;
                        const mKey = moment.unix(dateUnix).format('MMM YYYY');

                        if (!userTrendMap[userName]) {
                            userTrendMap[userName] = {};
                            last6MonthsKeys.forEach(k => userTrendMap[userName][k] = 0);
                        }
                        if (userTrendMap[userName][mKey] !== undefined) {
                            userTrendMap[userName][mKey]++;
                        }
                    });
                }

                // Sort by count descending
                const sortedUsers = Object.entries(userMap).sort((a, b) => b[1] - a[1]);

                // 1. Prepare Ranking Data (Top Items - Restored)
                // Use rankingRes (Selected Month)
                const itemMap = {};
                if (rankingRes && rankingRes.hits) {
                    rankingRes.hits.forEach(hit => {
                        const doc = hit.document;
                        let items = [];

                        // Robust cart parsing for Ranking
                        if (doc.cart && doc.cart.items && Array.isArray(doc.cart.items)) {
                            items = doc.cart.items;
                        } else if (doc.items && Array.isArray(doc.items)) {
                            items = doc.items;
                        } else if (doc.cart && typeof doc.cart === 'string') {
                            try {
                                const parsedCart = JSON.parse(doc.cart);
                                if (parsedCart && Array.isArray(parsedCart)) {
                                    items = parsedCart;
                                } else if (parsedCart && parsedCart.items && Array.isArray(parsedCart.items)) {
                                    items = parsedCart.items;
                                }
                            } catch (e) { /* ignore */ }
                        }

                        if (items && items.length > 0) {
                            items.forEach(item => {
                                if (item.cancelConfirm === true) return;
                                const name = item.title || item.name || "Unknown Product";
                                const id = item.id;
                                if (!itemMap[`${id}*${name}`]) itemMap[`${id}*${name}`] = 0;
                                itemMap[`${id}*${name}`] += (item.count || 0); // Aggregate quantity
                            });
                        }
                    });
                }

                // Sort by count desc
                const allItems = Object.entries(itemMap).map(([nameId, count]) => ({ id: nameId.split("*")[0], title: nameId.split("*")[1], total: count }));
                const grandTotal = allItems.reduce((sum, item) => sum + item.total, 0);

                const sortedRanking = allItems
                    .sort((a, b) => b.total - a.total)
                    .slice(0, 6)
                    .map(item => ({
                        ...item,
                        percent: grandTotal > 0 ? ((item.total / grandTotal) * 100).toFixed(1) : 0
                    }));

                setRankingData(sortedRanking);

                // 2. Prepare Occupation Data (Chart)
                const newOccupationData = [];
                sortedUsers.slice(0, 10).forEach(([name, count]) => {
                    newOccupationData.push({ userName: name, orderCount: count });
                });
                setOccupationData(newOccupationData);

                // 3. Prepare User Tabs Data (Top 5 Users)
                const newTabsData = [];
                sortedUsers.slice(0, 5).forEach(([name, count], index) => {
                    const trendDataForUser = [];
                    last6MonthsKeys.forEach(key => {
                        const val = userTrendMap[name] ? userTrendMap[name][key] : 0;
                        trendDataForUser.push({ month: key, orders: val });
                    });

                    newTabsData.push({
                        key: String(index + 1),
                        userName: name,
                        totalOrders: count,
                        trendData: trendDataForUser,
                        rank: index + 1
                    });
                });
                setUserTabsData(newTabsData);

                // --- Process Shipping Status Data (isShipped) ---
                let shippedCount = 0;
                let unshippedCount = 0;

                if (userStatsRes && userStatsRes.hits) {
                    userStatsRes.hits.forEach(hit => {
                        const doc = hit.document;
                        if (doc.isShipped === true) {
                            shippedCount++;
                        } else {
                            unshippedCount++;
                        }
                    });
                }

                setShippingData([
                    { type: "Shipped", count: shippedCount },
                    { type: "Unshipped", count: unshippedCount }
                ]);

                // --- Process Cancel / Return Data (Month by Month) ---
                const cancelReturnMap = {}; // "MMM YYYY" -> { cancel: 0, return: 0 }

                // Initialize last 3 months (inclusive of current)
                // If you want strictly last 3 distinct months:
                for (let i = 2; i >= 0; i--) {
                    const mKey = moment().subtract(i, 'months').format('MMM YYYY');
                    cancelReturnMap[mKey] = { cancel: 0, return: 0 };
                }

                if (cancelReturnRes && cancelReturnRes.hits) {
                    cancelReturnRes.hits.forEach(hit => {
                        const doc = hit.document;
                        let items = [];

                        // Robust cart parsing
                        if (doc.cart && doc.cart.items && Array.isArray(doc.cart.items)) {
                            items = doc.cart.items;
                        } else if (doc.items && Array.isArray(doc.items)) {
                            items = doc.items;
                        } else if (doc.cart && typeof doc.cart === 'string') {
                            try {
                                const parsedCart = JSON.parse(doc.cart);
                                if (parsedCart && Array.isArray(parsedCart)) {
                                    items = parsedCart;
                                } else if (parsedCart && parsedCart.items && Array.isArray(parsedCart.items)) {
                                    items = parsedCart.items;
                                }
                            } catch (e) { /* ignore */ }
                        }

                        if (items && items.length > 0) {
                            // Determine month key from sortDate
                            const dateUnix = doc.sortDate && doc.sortDate._seconds ? doc.sortDate._seconds : 0;
                            const mKey = moment.unix(dateUnix).format('MMM YYYY');

                            items.forEach(item => {
                                if (item.cancelConfirm === true) {
                                    if (cancelReturnMap[mKey]) cancelReturnMap[mKey].cancel += 1;
                                }
                                if (item.returnConfirm === true) {
                                    if (cancelReturnMap[mKey]) cancelReturnMap[mKey].return += 1;
                                }
                            });
                        }
                    });
                }

                const newCancelReturnData = [];
                // Iterate again to ensure chronological order
                for (let i = 2; i >= 0; i--) {
                    const mKey = moment().subtract(i, 'months').format('MMM YYYY');
                    if (cancelReturnMap[mKey]) {
                        newCancelReturnData.push({
                            month: mKey,
                            type: 'Cancel',
                            value: cancelReturnMap[mKey].cancel
                        });
                        newCancelReturnData.push({
                            month: mKey,
                            type: 'Return',
                            value: cancelReturnMap[mKey].return
                        });
                    }
                }
                setCancelReturnData(newCancelReturnData);



            } catch (error) {
                console.error("Error fetching typesense data:", error);
            } finally {
                setLoading(false);
            }
        };

        fetchOrderCount();
    }, [selectedDate, props.userOrg, props.org, props.user]);

    // Mock Data




    return (
        <div style={{ padding: '10px', background: '#f5f5f5', minHeight: '100vh' }}>

            {/* Header Section */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24, background: '#fff', padding: '16px 24px' }}>
                <Title level={4} style={{ margin: 0 }}>Analytics Dashboard</Title>
                <Button>Export Data</Button>
            </div>

            {/* Filter Section
            <Card bordered={false} style={{ marginBottom: 24 }}>
                <Row gutter={16} align="middle">
                    <Col>
                        <Text>Department: </Text>
                        <Select defaultValue="tech" style={{ width: 150, marginLeft: 8 }}>
                            <Option value="tech">Tech Dept</Option>
                            <Option value="sales">Sales Dept</Option>
                        </Select>
                    </Col>
                    <Col>
                        <Text>Created Time: </Text>
                        <DatePicker picker="month" style={{ marginLeft: 8 }} value={selectedDate} onChange={setSelectedDate} />
                    </Col>
                    <Col style={{ marginLeft: 'auto' }}>
                        <Button icon={<ReloadOutlined />} style={{ marginRight: 8 }}>Reset</Button>
                        <Button type="primary" icon={<SearchOutlined />}>Search</Button>
                    </Col>
                </Row>
            </Card> */}

            {/* Key Metrics */}
            <Row gutter={24} style={{ marginBottom: 24 }}>
                <Col span={6}>
                    <Card bordered={false} loading={loading}>
                        <Statistic
                            title="Today Orders"
                            value={stats.today.value}
                            precision={0}
                            valueStyle={{ color: '#000', fontWeight: 'bold' }}
                            suffix={
                                <Text type={stats.today.change >= 0 ? "success" : "danger"} style={{ fontSize: 12 }}>
                                    {stats.today.change >= 0 ? <ArrowUpOutlined /> : <ArrowDownOutlined />} {Math.abs(stats.today.change).toFixed(2)}%
                                </Text>
                            }
                        />
                        <Text type="secondary" style={{ fontSize: 12 }}>Compared to Yesterday</Text>
                    </Card>
                </Col>
                <Col span={6}>
                    <Card bordered={false} loading={loading}>
                        <Statistic
                            title="Yesterday Orders"
                            value={stats.yesterday.value}
                            precision={0}
                            valueStyle={{ color: '#000', fontWeight: 'bold' }}
                            suffix={
                                <Text type={stats.yesterday.change >= 0 ? "success" : "danger"} style={{ fontSize: 12 }}>
                                    {stats.yesterday.change >= 0 ? <ArrowUpOutlined /> : <ArrowDownOutlined />} {Math.abs(stats.yesterday.change).toFixed(2)}%
                                </Text>
                            }
                        />
                        <Text type="secondary" style={{ fontSize: 12 }}>Compared to Day Before</Text>
                    </Card>
                </Col>
                <Col span={6}>
                    <Card bordered={false} loading={loading}>
                        <Statistic
                            title="This Month Orders"
                            value={stats.month.value}
                            precision={0}
                            valueStyle={{ color: '#000', fontWeight: 'bold' }}
                            suffix={
                                <Text type={stats.month.change >= 0 ? "success" : "danger"} style={{ fontSize: 12 }}>
                                    {stats.month.change >= 0 ? <ArrowUpOutlined /> : <ArrowDownOutlined />} {Math.abs(stats.month.change).toFixed(2)}%
                                </Text>
                            }
                        />
                        <Text type="secondary" style={{ fontSize: 12 }}>Compared to Prev Month</Text>
                    </Card>
                </Col>
                <Col span={6}>
                    <Card bordered={false} loading={loading}>
                        <Statistic
                            title="Last 7 Days Orders"
                            value={stats.last7Days.value}
                            precision={0}
                            valueStyle={{ color: '#000', fontWeight: 'bold' }}
                            suffix={
                                <Text type={stats.last7Days.change >= 0 ? "success" : "danger"} style={{ fontSize: 12 }}>
                                    {stats.last7Days.change >= 0 ? <ArrowUpOutlined /> : <ArrowDownOutlined />} {Math.abs(stats.last7Days.change).toFixed(2)}%
                                </Text>
                            }
                        />
                        <Text type="secondary" style={{ fontSize: 12 }}>Last 30 Days: {stats.last30Days.value} ({stats.last30Days.change >= 0 ? "+" : ""}{stats.last30Days.change.toFixed(2)}%)</Text>
                    </Card>
                </Col>
            </Row>

            <Row gutter={24} style={{ marginBottom: 24 }}>
                <Col span={24}>
                    <Card title="Order Trend" extra={<Select defaultValue="12m" variant="borderless"><Option value="12m">Last 12 Months</Option></Select>} bordered={false} loading={loading}>
                        <Line
                            data={trendData}
                            xField="month"
                            yField="value"
                            seriesField="type"
                            smooth={true}
                            height={300}
                            color={['#52c41a', '#f5222d', '#ff63e0', '#00c2ff']} // Green (Shipped), Yellow (Unshipped), Red (Cancel), Blue (Return)
                            legend={{ position: 'top' }}
                            yAxis={{
                                label: {
                                    formatter: (v) => `${v}`, // Display plain number (count)
                                },
                            }}
                        />
                    </Card>
                </Col>
            </Row>

            {/* Main Charts */}
            <Row gutter={24} style={{ marginBottom: 24 }}>
                <Col span={16}>
                    <Card title="Order Amount Trend" extra={<Select defaultValue="6m" variant="borderless"><Option value="6m">Last 6 Months</Option></Select>} bordered={false} loading={loading}>
                        <Area
                            data={overallTrendData}
                            xField="month"
                            yField="sales"
                            smooth={true}
                            height={300}
                            color="#4285F4"
                            yAxis={{
                                label: {
                                    formatter: (v) => `${v}`.replace(/\B(?=(\d{3})+(?!\d))/g, ','),
                                },
                            }}
                            tooltip={{
                                formatter: (datum) => {
                                    return { name: 'Sales', value: (datum.sales || 0).toLocaleString() };
                                },
                            }}
                        />
                    </Card>
                </Col>
                <Col span={8}>
                    <Card title="Top Items" extra={<Select defaultValue="6m" variant="borderless"><Option value="6m">Last 6 Months</Option></Select>} bordered={false} loading={loading}>
                        <List
                            itemLayout="horizontal"
                            dataSource={rankingData}
                            renderItem={(item, index) => (
                                <List.Item>
                                    <div style={{ width: '100%', display: 'flex', alignItems: 'center' }}>
                                        <div style={{
                                            width: 20, height: 20, borderRadius: '50%',
                                            background: index < 3 ? '#314659' : '#f0f2f5',
                                            color: index < 3 ? '#fff' : '#000',
                                            textAlign: 'center', lineHeight: '20px', marginRight: 16, fontSize: 12
                                        }}>
                                            {index + 1}
                                        </div>
                                        <Text style={{ width: 170, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }} title={item.title}>
                                            <a href={`/items/${item.id}`} target="_blank">{item.title}</a>
                                        </Text>
                                        <Progress
                                            percent={item.percent}
                                            strokeColor="#6395fa"
                                            style={{ flex: 1, marginRight: 8, width: 100 }}
                                            format={(percent) => `${percent}%`} // Show percentage
                                        />
                                    </div>
                                </List.Item>
                            )}
                        />
                    </Card>
                </Col>
            </Row>

            {/* Secondary Charts */}
            <Row gutter={24} style={{ marginBottom: 24 }}>
                <Col span={8}>
                    <Card title="Cancel / Return" extra={<Select defaultValue="6m" variant="borderless"><Option value="6m">Last 3 Months</Option></Select>} bordered={false} loading={loading}>
                        <Column
                            data={cancelReturnData}
                            xField="month"
                            yField="value"
                            seriesField="type"
                            isGroup={true}
                            height={300}
                            color={['#ff63e0', '#00c2ff']}
                        />
                    </Card>
                </Col>
                <Col span={8}>
                    <Card title="Shipping Status" extra={<Select defaultValue="6m" variant="borderless"><Option value="6m">Last 6 Months</Option></Select>} bordered={false} loading={loading}>
                        <Pie
                            data={shippingData}
                            angleField="count"
                            colorField="type"
                            innerRadius={0.6}
                            radius={0.8}
                            height={300}
                            color={['#34A853', '#EA4335']}
                        />
                    </Card>
                </Col>
                <Col span={8}>
                    <Card title="User Orders" extra={<Select defaultValue="6m" variant="borderless"><Option value="6m">Last 6 Months</Option></Select>} bordered={false} loading={loading}>
                        <Bar
                            data={occupationData}
                            xField="orderCount"
                            yField="userName"
                            height={300}
                            legend={false}
                        />
                    </Card>
                </Col>
            </Row>

            {/* Detailed Analysis Section */}
            <Card title="Top 5 User" bordered={false} loading={loading} extra={<Select defaultValue="6m" variant="borderless"><Option value="6m">Last 6 Months</Option></Select>}>
                <Tabs defaultActiveKey="1">
                    {userTabsData.length > 0 ? (
                        userTabsData.map(userTab => (
                            <TabPane tab={userTab.userName} key={userTab.key}>
                                <Row gutter={24}>
                                    <Col span={16}>
                                        <div style={{ display: 'flex', alignItems: 'flex-baseline', marginBottom: 16 }}>
                                            <Title level={3} style={{ margin: 0, marginRight: 16 }}>{userTab.totalOrders}</Title>
                                            <Text type="secondary">Total Orders (Last 6 Months)</Text>
                                        </div>
                                        {/* <Progress percent={86.2} strokeColor="#4285F4" showInfo={false} style={{ marginBottom: 24 }} /> */}

                                        <Title level={5}>Order Trend (Last 6 Months)</Title>
                                        <Area
                                            data={userTab.trendData}
                                            xField="month"
                                            yField="orders"
                                            height={300}
                                            color="#8884d8"
                                            smooth={true}
                                        />
                                    </Col>
                                    <Col span={8}>
                                        <Title level={5}>User Analysis</Title>
                                        <List>
                                            <List.Item><Text><InfoCircleOutlined /> Rank: #{userTab.rank}</Text></List.Item>
                                            <List.Item><Text><InfoCircleOutlined /> High activity observed.</Text></List.Item>
                                            {/* <List.Item><Text><InfoCircleOutlined /> Recommendation: Offer loyalty reward.</Text></List.Item> */}
                                        </List>
                                    </Col>
                                </Row>
                            </TabPane>
                        ))
                    ) : (
                        <TabPane tab="No Data" key="1">
                            <Text>No user activity found in the last 6 months.</Text>
                        </TabPane>
                    )}
                </Tabs>
            </Card>
        </div>
    );
};

const DropdownText = () => <span style={{ cursor: 'pointer' }}>Last month <ArrowDownOutlined style={{ fontSize: 10 }} /></span>;

export default Dashboard;
